import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { QuestionnaireVariant } from '@aldebaran/prisma';

import omit from 'lodash/omit';
import uniqBy from 'lodash/uniqBy';
import { isValid } from 'date-fns';

import { client } from '../util/client';

import {
  ChecklistItemState,
  GraphState,
  QuestionResponseStore,
  QuestionnaireState,
  QuestionnaireSummary,
  RootState,
} from '../types/graph';

import { useCheckCondition } from './useCheckCondition';
import { useVersion } from './useVersion';
import { useStatistics } from './useStatistics';
import { valueKeys } from '../util/graph';
import { AxiosError } from 'axios';
import { useMutation, useQuery } from 'react-query';
import { ConsultationMotive, Report } from '../types/global';
import { enqueueQuestionnaireSave } from '../util/questionnaireSaveQueue';
import {
  setQuestionnaireConflict,
  updateQuestionnaireId,
  updateQuestionnaireRevision,
} from '../reducers/graph';
import { Patient, Practice, Professional } from '@aldebaran/prisma';
import { useWaitingRoom } from './useMode';
import {
  useCreatePatient,
  useSelectPatient,
  useSelectedPatient,
} from './patient.hooks';
import { pick } from 'lodash';
import { QuestionnaireVariantFlagMap } from '@aldebaran/common/qVariants';
import { useStandaloneReports } from './useStandaloneReports';
import { getDemoRequestId } from '@/components/DemoLead/demoLeadStorage';
import { isWebsiteDemoPractice } from '@/util/demoPractice';
import * as Sentry from '@sentry/react';
import { StructuredQuestionnaireData } from '@aldebaran/common/types/questionnaire';
import {
  computeConsultationMotives,
  computeQuestionnaireSummary,
  computeReport,
  computeStructuredQuestionnaireData,
} from '../util/questionnaire-computations';

const excludedQuestions = ['CONSULTATION_DATE', 'CONSULTATION_TIME'];

const patientProfileKeys = {
  '[Prestation: #00‹]-(a_profil)->[Profil: Alice]': 'ALICE',
  '[Prestation: #00‹]-(a_profil)->[Profil: Bob]': 'BOB',
  '[Prestation: #00‹]-(a_profil)->[Profil: Charlie]': 'CHARLIE',
  '[Prestation: #00‹]-(a_profil)->[Profil: Daniel]': 'DANIEL',
};

const lastConsultationKeys = {
  '[Prestation: #99‹]-(ANC)->[Ancienneté]-(DUR)->[Durée: t0-t99 #‹]-(≤)->[Valeur: 1]-(unité)->[Unité: semaine]':
    '1_WEEK',
  '[Prestation: #99‹]-(ANC)->[Ancienneté]-(DUR)->[Durée: t0-t99 #‹]-(≤)->[Valeur: 1]-(unité)->[Unité: mois]':
    '1_MONTH',
  '[Prestation: #99‹]-(ANC)->[Ancienneté]-(DUR)->[Durée: t0-t99 #‹]-(≤)->[Valeur: 3]-(unité)->[Unité: mois]':
    '3_MONTHS',
  '[Prestation: #99‹]-(ANC)->[Ancienneté]-(DUR)->[Durée: t0-t99 #‹]-(≤)->[Valeur: 6]-(unité)->[Unité: mois]':
    '6_MONTHS',
  '[Prestation: #99‹]-(ANC)->[Ancienneté]-(DUR)->[Durée: t0-t99 #‹]-(≤)->[Valeur: 12]-(unité)->[Unité: mois]':
    '1_YEAR',
  '[Prestation: #99‹]-(ANC)->[Ancienneté]-(DUR)->[Durée: t0-t99 #‹]-(≥)->[Valeur: 12]-(unité)->[Unité: mois]':
    'MORE_THAN_1_YEAR',
};

const lastConsultationGreaterThanKey =
  '[Prestation: #99‹]-(ANC)->[Ancienneté]-(DUR)->[Durée: t0-t99 #‹]-(≥)->[Valeur: *]';
const lastConsultationLessThanKey =
  '[Prestation: #99‹]-(ANC)->[Ancienneté]-(DUR)->[Durée: t0-t99 #‹]-(≤)->[Valeur: *]';

const getLastConsultation = (values: RootState['graph']['values']) => {
  const lessThan = values[lastConsultationLessThanKey];
  const greaterThan = values[lastConsultationGreaterThanKey];

  if (lessThan) {
    const unit = lessThan.unit?.plural;
    const value = lessThan.value;
    if (unit === 'semaine' && value === 1) return '1_WEEK';
    if (unit === 'mois') {
      if (value === 1) return '1_MONTH';
      if (value === 3) return '3_MONTHS';
      if (value === 6) return '6_MONTHS';
      if (value === 12) return '1_YEAR';
    }
  }
  if (greaterThan) {
    return 'MORE_THAN_1_YEAR';
  }
};

type ReportInput = {
  data: Report[];
  questionnaire: QuestionnaireSummary;
  graph: GraphState;
  report: string;
  questionnaireOptionsSnapshot?: Partial<QuestionnaireVariantFlagMap>;
};
type ConsultationInput = {
  checkedIn: boolean;
  ready: boolean;
  complete: boolean;
  date?: Date;
  newPatient: boolean;
  professionalId?: Professional['id'];
  practiceId: Practice['id'];
  motives: ConsultationMotive['id'][];
  version: string;
  profile?: (typeof patientProfileKeys)[keyof typeof patientProfileKeys];
  lastConsultation?: (typeof lastConsultationKeys)[keyof typeof lastConsultationKeys];
  referrer?: string;
  noAppointment?: boolean;
};

interface UpdateQuestionnaireInput {
  id: string | undefined;
  version: string;
  variant: QuestionnaireVariant;
  patientId: Patient['id'];
  consultation: ConsultationInput;
  report: ReportInput;
  data: QuestionnaireSummary;
  state: QuestionnaireState;
  graph: GraphState;
  responses: QuestionResponseStore;
  orderedResponses: string[];
  checklistItems: ChecklistItemState[];
  structuredQuestionnaireData?: StructuredQuestionnaireData;
  /** Website-demo lead that started this questionnaire */
  demoRequestId?: string;
}

export const useDetermineNewPatient = () => {
  const check = useCheckCondition();
  const newPatient =
    check('[Prestation: #00‹]-(a_occurrence)->[Occurrence: N=1]') ||
    check('[Prestation: #00‹]-(a_occurrence)->[Occurrence: N<=1]');
  return newPatient;
};

export const useChecklist = () => {
  const checklistItems = useSelector(
    (state: RootState) => state.questionnaire.checklistItems ?? {},
  );

  return useMemo(
    () => uniqBy(Object.values(checklistItems).flat(), 'value'),
    [checklistItems],
  );
};

const excludedValues = [
  valueKeys.firstName,
  valueKeys.lastName,
  valueKeys.dateOfBirth,
];

interface UseUpdateQuestionnaireOptions {
  onSuccess?: () => void;
  onError?: (error?: AxiosError) => void;
  complete?: boolean;
}

// Rejects a save that was still queued when an earlier one hit a conflict
class SaveDroppedAfterConflict extends Error {}

export const useUpdateQuestionnaire = ({
  onSuccess,
  onError,
  complete = false,
}: UseUpdateQuestionnaireOptions) => {
  const dispatch = useDispatch();
  const store = useStore();
  const version = useVersion();

  const questionnaireId = useSelector(
    (state: RootState) => state.questionnaireId,
  );
  const userResponses = useSelector((state: RootState) => state.userResponses);
  const questionnaire = useSelector((state: RootState) => state.questionnaire);
  const referrer = useSelector((state: RootState) => state.referrer);
  const checklistItems = useChecklist();

  const secondPageQuestions = useMemo(
    () =>
      questionnaire.totalQueue.length >= 2
        ? Object.values(questionnaire.totalQueue[1]).flat()
        : [],
    [questionnaire.totalQueue],
  );
  const hasAnsweredThreeQuestionsOnSecondPage = useMemo(
    () =>
      secondPageQuestions.filter(
        (questionId) =>
          questionId !== undefined &&
          questionId in userResponses &&
          Object.keys(userResponses[questionId]).length > 0, // case where the user deletes his answer (the questionId is here but it's an empty object )
      ).length > 2,
    [secondPageQuestions, userResponses],
  );

  /* Ready if questionnaire has more than 2 pages (starts at maxPage = 0) 
  OR has answered at least 3 questions on the second page */
  const ready =
    questionnaire.maxPage > 1 ||
    (questionnaire.maxPage === 1 && hasAnsweredThreeQuestionsOnSecondPage);
  const waitingRoom = useWaitingRoom();
  const { data: patient } = useSelectedPatient();
  const { mutate: createPatient } = useCreatePatient(true);
  const { mutate: selectPatient } = useSelectPatient();

  const recordStatistics = useStatistics();

  const newPatient = useDetermineNewPatient();

  const standaloneReports = useStandaloneReports();
  const { data: motivesList = [] } = useQuery<ConsultationMotive[]>(
    'consultationMotives',
    async () =>
      client('/consultationMotives', { params: { version } }).then(
        (response) => response?.data,
      ),
  );

  const handleSuccess = useCallback(() => {
    onSuccess?.();
  }, [onSuccess]);

  const handleError = useCallback(
    (err: AxiosError) => {
      if (err instanceof SaveDroppedAfterConflict) return;
      onError?.(err);
      recordStatistics({ section: 'SUBMISSION_ERROR' });
    },
    [onError, recordStatistics],
  );

  const { mutate, ...rest } = useMutation({
    // Id and revision are read when the save is sent, not when it is queued
    mutationFn: (input: UpdateQuestionnaireInput) =>
      enqueueQuestionnaireSave(async () => {
        const {
          questionnaireId: id,
          questionnaireRevision: revision,
          questionnaireConflict,
        } = store.getState() as RootState;
        // A save queued behind the one that hit a conflict is stale too
        if (questionnaireConflict) throw new SaveDroppedAfterConflict();
        try {
          const { data } = await (id
            ? client.put('/questionnaire/' + id, { ...input, id, revision })
            : client.post('/questionnaire', input));
          dispatch(updateQuestionnaireId(data.id));
          dispatch(updateQuestionnaireRevision(data.revision));
          return data;
        } catch (error) {
          // Another device saved more recently: stop saving from this one.
          // Flagged here, before the next queued save runs.
          if ((error as AxiosError).response?.status === 409)
            dispatch(setQuestionnaireConflict());
          throw error;
        }
      }),
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const formatDebugReportData = (reportData: Report[]) => {
    return reportData
      .sort((a, b) => a.classification.localeCompare(b.classification))
      .map((r) => `${r.classification}: ${r.value}`)
      .join('\n');
  };

  const updateQuestionnaire = useCallback(() => {
    const freshState = store.getState() as RootState;
    if (freshState.questionnaireConflict) return;
    const freshGraph = freshState.graph;
    const freshQuestionnaire = freshState.questionnaire;
    const freshUserResponses = freshState.userResponses;
    const freshKB = freshState.kb;
    const freshConsultation = freshState.consultation;

    const date = new Date(Number(freshGraph.values[valueKeys.date]?.value));
    const professionalId = freshConsultation.professional?.id;
    const practiceId = freshConsultation.practice?.id;

    if (!version || !practiceId || !patient) {
      onError?.();
      return;
    }

    const isKBReady =
      freshKB.formulasLoaded &&
      Object.keys(freshKB.questions).length > 0 &&
      Object.keys(freshKB.responses).length > 0;

    if (!isKBReady) {
      Sentry.captureMessage('KB not loaded at questionnaire submission time', {
        level: 'warning',
        extra: {
          formulasLoaded: freshKB.formulasLoaded,
          questionsCount: Object.keys(freshKB.questions).length,
          responsesCount: Object.keys(freshKB.responses).length,
          questionnaireId,
          complete,
          timestamp: new Date().toISOString(),
        },
      });
      onError?.();
      return;
    }

    const freshReport = computeReport({
      userResponses: freshUserResponses,
      kbResponses: freshKB.responses,
      graph: freshGraph,
      formulas: freshKB.formulas,
      standaloneReports,
      consultation: freshConsultation,
    });

    const freshSections = freshQuestionnaire.config
      ? Object.values(freshQuestionnaire.config.pages).flatMap(
          (page) => (page as { sections: string[] }).sections,
        )
      : [];

    const freshQuestionnaireSummary = computeQuestionnaireSummary({
      userResponses: freshUserResponses,
      kbQuestions: freshKB.questions,
      sectionAssignments: freshQuestionnaire.sectionAssignments,
      sections: freshSections,
    });

    const freshCurrentPage = freshGraph.currentPage;
    const freshCurrentPageSections =
      freshQuestionnaire.config?.pages[freshCurrentPage]?.sections || [];
    const freshActiveQueue = freshCurrentPageSections.flatMap(
      (section) => freshQuestionnaire.sectionQueues[section] || [],
    );

    const freshStructuredQuestionnaireData = computeStructuredQuestionnaireData(
      {
        userResponses: freshUserResponses,
        kbQuestions: freshKB.questions,
        kbResponses: freshKB.responses,
        totalQueue: freshQuestionnaire.totalQueue,
        activeQueue: freshActiveQueue,
        currentPage: freshCurrentPage,
        config: freshQuestionnaire.config,
      },
    );

    const freshConsultationMotives = computeConsultationMotives({
      report: freshReport,
      motivesList,
    });

    // Compute fresh orderedQuestionIds from fresh state
    const freshOrderedQuestionIds: string[] = [];
    freshQuestionnaire.totalQueue.forEach((item) => {
      Object.values(item)
        .flat()
        .forEach((key) => {
          // eslint-disable-next-line no-prototype-builtins
          if (key && freshUserResponses.hasOwnProperty(key)) {
            freshOrderedQuestionIds.push(key);
          }
        });
    });
    // Add any remaining questions from current page that are not already included
    freshActiveQueue.forEach((key) => {
      if (
        // eslint-disable-next-line no-prototype-builtins
        freshUserResponses.hasOwnProperty(key) &&
        !freshOrderedQuestionIds.includes(key)
      ) {
        freshOrderedQuestionIds.push(key);
      }
    });

    const profileKey = freshGraph.assertions.find((a) =>
      Object.keys(patientProfileKeys).includes(a),
    ) as keyof typeof patientProfileKeys;

    const formattedReport = formatDebugReportData(freshReport);

    // DEBUG FOR COMPLETE QUESTIONNAIRE WITHOUT COMPLAINTS
    if (complete) {
      const complaintsCount = freshReport.filter(
        (r) => r.classification?.startsWith('C.'),
      ).length;
      const hasDELSections = freshReport.some(
        (r) =>
          r.classification?.startsWith('D.') ||
          r.classification?.startsWith('E.') ||
          r.classification?.startsWith('L.'),
      );

      if (complaintsCount < 3 && hasDELSections) {
        Sentry.captureMessage(
          'BUG: Report complaints page seems to be incomplete',
          {
            level: 'error',
            extra: {
              practiceId: freshConsultation.practice?.id,
              professionalId: freshConsultation.professional?.id,
              questionnaireId: questionnaireId,
              complaintsCount,
              reportClassifications: freshReport.map((r) => r.classification),
              currentPage: freshGraph.currentPage,
              maxPage: freshQuestionnaire.maxPage,
              totalQueue: freshQuestionnaire.totalQueue,
              answeredQuestionIds: Object.keys(freshUserResponses),
              variant: freshGraph.variant,
              version: version,
              date: date,
              time: new Date().toISOString(),
            },
          },
        );
      }
    }

    mutate(
      {
        id: questionnaireId,
        version: version.toString(),
        variant: freshGraph.variant,
        patientId: patient.id,
        // Website-demo practice only
        demoRequestId: isWebsiteDemoPractice(freshConsultation.practice?.slug)
          ? getDemoRequestId()
          : undefined,
        consultation: {
          // The client must never invent an appointment date (ALDO-2611). The
          // assisted-launch flow may not have either a graph or scheduled date;
          // omit it rather than fabricating one from the browser clock.
          date: isValid(date)
            ? date
            : freshConsultation.date
            ? new Date(freshConsultation.date)
            : undefined,
          version: version.toString(),
          newPatient,
          practiceId,
          professionalId,
          motives: freshConsultationMotives,
          checkedIn: waitingRoom && freshQuestionnaire.maxPage > 0,
          ready,
          complete,
          profile: profileKey ? patientProfileKeys[profileKey] : undefined,
          lastConsultation: getLastConsultation(freshGraph.values),
          referrer,
          noAppointment: freshConsultation.noAppointment,
        },
        report: {
          data: freshReport,
          questionnaire: freshQuestionnaireSummary,
          graph: {
            ...freshGraph,
            assertions: freshGraph.assertions,
            values: omit(freshGraph.values, excludedValues),
          },
          report: formattedReport,
          questionnaireOptionsSnapshot: freshGraph.personalizationFlags,
        },
        data: freshQuestionnaireSummary,
        graph: freshGraph,
        state: {
          ...freshQuestionnaire,
          totalQueue: (() => {
            const currentPage = freshGraph.currentPage;
            const currentPageSections =
              freshQuestionnaire.config?.pages[currentPage]?.sections || [];
            const activeQueue = currentPageSections.flatMap(
              (section) => freshQuestionnaire.sectionQueues[section] || [],
            );
            if (!currentPage || activeQueue.length === 0)
              return freshQuestionnaire.totalQueue;
            const idx = freshQuestionnaire.totalQueue.findIndex(
              // eslint-disable-next-line no-prototype-builtins
              (item) => item.hasOwnProperty(currentPage),
            );
            const updated = [...freshQuestionnaire.totalQueue];
            if (idx !== -1) {
              updated[idx] = { [currentPage]: activeQueue };
            } else {
              updated.push({ [currentPage]: activeQueue });
            }
            return updated;
          })(),
        },
        responses: omit(freshUserResponses, excludedQuestions),
        checklistItems,
        structuredQuestionnaireData: freshStructuredQuestionnaireData,
        orderedResponses: freshOrderedQuestionIds,
      },
      {
        onError: (error) => {
          if (error.response?.status === 403) {
            dispatch(updateQuestionnaireId(undefined));
            createPatient(
              pick(patient, [
                'self',
                'firstName',
                'lastName',
                'dateOfBirth',
                'sex',
              ]),
              {
                onSuccess: (newPatient) => selectPatient(newPatient?.id),
              },
            );
          }
        },
      },
    );
  }, [
    store,
    patient,
    createPatient,
    selectPatient,
    dispatch,
    version,
    mutate,
    questionnaireId,
    newPatient,
    ready,
    complete,
    waitingRoom,
    referrer,
    checklistItems,
    standaloneReports,
    motivesList,
    onError,
  ]);

  return { updateQuestionnaire, ...rest };
};

export const useCancelQuestionnaire = () =>
  useMutation({
    mutationFn: (id: string) =>
      client.delete(`/questionnaire/` + id).then((res) => res.data),
  });
